import { Component, effect, inject, signal } from '@angular/core';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { ThemeService } from '../../../../core/services/theme.service';
import { DashboardService } from '../../../../core/services/dashboard/dashboard.service';
import { DashboardStatCardComponent } from '../dashboard-stat-card/dashboard-stat-card.component';
import { DashboardQuickActionsComponent } from '../dashboard-quick-actions/dashboard-quick-actions.component';
import { DashboardRecentListComponent } from '../dashboard-recent-list/dashboard-recent-list.component';
import { DashboardSelectOrgPromptComponent } from '../dashboard-select-org-prompt/dashboard-select-org-prompt.component';
import { DashboardChartComponent } from '../dashboard-chart/dashboard-chart.component';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { RouterLink } from '@angular/router';
import { DashboardGlobalStats, DashboardOrganizationStats, DashboardQuickAction, DashboardRecentItem } from '../../../../core/models/ui/dashboard.model';
import { DashboardConstants } from '../../../../core/constants/dashboard.constants';
import { DashboardChartConfig, DashboardChartUtilities } from '../../../../core/utilities/dashboard-chart.utilities';

@Component({
	selector: 'ms-staff-dashboard',
	imports: [
		DashboardStatCardComponent,
		DashboardQuickActionsComponent,
		DashboardRecentListComponent,
		DashboardSelectOrgPromptComponent,
		DashboardChartComponent,
		FaDuotoneIconComponent,
		RouterLink
	],
	templateUrl: './staff-dashboard.component.html',
	styleUrl: './staff-dashboard.component.scss'
})
export class StaffDashboardComponent {

	//#region Signals

	public globalStats = signal<DashboardGlobalStats | undefined>(undefined);
	public orgStats = signal<DashboardOrganizationStats | undefined>(undefined);
	public recentUsers = signal<DashboardRecentItem[]>([]);
	public recentMembers = signal<DashboardRecentItem[]>([]);

	public isLoadingGlobal = signal(true);
	public isLoadingOrg = signal(false);
	public isLoadingUsers = signal(true);
	public isLoadingMembers = signal(false);

	public pricingTierData = signal<DashboardChartConfig | undefined>(undefined);
	public invoiceStatusData = signal<DashboardChartConfig | undefined>(undefined);
	public memberStatusData = signal<DashboardChartConfig | undefined>(undefined);
	public isLoadingPricingTier = signal(true);
	public isLoadingInvoiceStatus = signal(false);
	public isLoadingMemberStatus = signal(false);

	//#endregion

	//#region Constants

	public DashboardConstants = DashboardConstants;

	public get globalQuickActions(): DashboardQuickAction[] {
		return DashboardConstants.STAFF_GLOBAL_QUICK_ACTIONS;
	}

	public get orgQuickActions(): DashboardQuickAction[] {
		return DashboardConstants.STAFF_ORG_QUICK_ACTIONS;
	}

	//#endregion

	private dashboardService = inject(DashboardService);
	public userSessionService = inject(UserSessionService);
	private themeService = inject(ThemeService);

	constructor() {
		this.loadGlobalData();

		effect(() => {
			const org = this.userSessionService.activeOrganization();
			if (org) {
				this.loadOrganizationData(org.organizationId);
				this.loadOrgCharts(org.organizationId);
			} else {
				this.orgStats.set(undefined);
				this.recentMembers.set([]);
				this.invoiceStatusData.set(undefined);
				this.memberStatusData.set(undefined);
			}
		});
	}

	//#region Data Loading

	private async loadGlobalData(): Promise<void> {
		this.isLoadingGlobal.set(true);
		this.isLoadingUsers.set(true);
		this.isLoadingPricingTier.set(true);

		const statsPromise = this.dashboardService.getGlobalStats().then(result => {
			if (result.success) {
				this.globalStats.set(result.data);
			}
			this.isLoadingGlobal.set(false);
		});

		const usersPromise = this.dashboardService.getRecentUsers().then(result => {
			if (result.success) {
				this.recentUsers.set(result.data ?? []);
			}
			this.isLoadingUsers.set(false);
		});

		const pricingPromise = this.dashboardService.getOrganizationsByPricingTier().then(result => {
			if (result.success && result.data) {
				this.pricingTierData.set(DashboardChartUtilities.buildPricingTierChartData(result.data, this.themeService));
			}
			this.isLoadingPricingTier.set(false);
		});

		await Promise.all([statsPromise, usersPromise, pricingPromise]);
	}

	private async loadOrganizationData(organizationId: number): Promise<void> {
		this.isLoadingOrg.set(true);
		this.isLoadingMembers.set(true);

		const statsPromise = this.dashboardService.getOrganizationStats(organizationId).then(result => {
			if (result.success) {
				this.orgStats.set(result.data);
			}
			this.isLoadingOrg.set(false);
		});

		const membersPromise = this.dashboardService.getRecentMembers(organizationId).then(result => {
			if (result.success) {
				this.recentMembers.set(result.data ?? []);
			}
			this.isLoadingMembers.set(false);
		});

		await Promise.all([statsPromise, membersPromise]);
	}

	private async loadOrgCharts(organizationId: number): Promise<void> {
		this.isLoadingInvoiceStatus.set(true);
		this.isLoadingMemberStatus.set(true);

		const invoicePromise = this.dashboardService.getInvoiceStatusCounts(organizationId).then(result => {
			if (result.success && result.data) {
				this.invoiceStatusData.set(DashboardChartUtilities.buildInvoiceStatusChartData(result.data, this.themeService));
			}
			this.isLoadingInvoiceStatus.set(false);
		});

		const memberStatusPromise = this.dashboardService.getMemberStatusCounts(organizationId, true).then(result => {
			if (result.success && result.data) {
				this.memberStatusData.set(DashboardChartUtilities.buildMemberStatusChartData(result.data, this.themeService));
			}
			this.isLoadingMemberStatus.set(false);
		});

		await Promise.all([invoicePromise, memberStatusPromise]);
	}

	//#endregion
}
